import GlobalVariablesDialog from '../variables/GlobalVariablesDialog';
import ConnectionLimitsDialog from '../connections/ConnectionLimitsDialog';
import ConnectionsDialog from '../connections/ConnectionsDialog';
import React, { useState } from 'react';
import Header from './Header';
import WorkflowExplorer from './WorkflowExplorer';
import RunStatusSync from '../workflow/RunStatusSync';
import DataDefinitionModal from '../workflow/panels/DataDefinition/DataDefinitionModal';
import TemplateManagerDialog from '../workflow/wizard/TemplateManagerDialog';
import ServerExplorerDialog from '../workflow/panels/DataDefinition/ServerExplorerDialog';
import { DataNodeViewHost } from '../workflow/panels/PropertyPanel/DataNodeDataModal';
import { WorkflowPreviewHost } from '../workflow/WorkflowPreviewModal';
import PropertyDock from './PropertyDock';
import CollapsibleSide from './CollapsibleSide';
import { useDockPrefs } from './dockPrefs';
import AiDrawer from './AiDrawer';
import { setAiDrawer, useAiDrawer } from './aiDrawerState';
import { useAssistantEnabled } from '../../hooks/useAssistantEnabled';
import BulkEditScreen from '../bulk/BulkEditScreen';
import BulkCreateScreen from '../bulk/BulkCreateScreen';
import { useTranslation } from 'react-i18next';
import store from '../../store';
import { checkOpenWorkflowFreshness } from '../../store/workflow/openWorkflow';
import { showAlert } from '../common/AppDialog';
import ProjectMenu from './ProjectMenu';

interface ModernLayoutProps {
  children: {
    toolbox: React.ReactNode;
    canvas: React.ReactNode;
    property: React.ReactNode;
  };
}

const ModernLayout: React.FC<ModernLayoutProps> = ({ children }) => {
  // AI 에이전트 — 스탠드얼론은 실행 중 확인(내부망이면 감춘다, hooks/useAssistantEnabled)
  const assistantOn = useAssistantEnabled();
  const { t } = useTranslation();
  const [varsOpen, setVarsOpen] = useState(false);
  const [dataDefModalOpen, setDataDefModalOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [explorerOpen, setExplorerOpen] = useState(false);
  const [connLimitsOpen, setConnLimitsOpen] = useState(false);
  const [connsOpen, setConnsOpen] = useState(false);
  /** 속성 일괄수정 — 시스템 버튼 줄에서 팝업으로 연다 (여러 워크플로를 한 번에 다루므로 캔버스 위가 아니라 별도 화면) */
  const [bulkOpen, setBulkOpen] = useState(false);
  /** 워크플로 일괄 등록 — 템플릿 하나로 여러 워크플로를 만든다 */
  const [bulkNewOpen, setBulkNewOpen] = useState(false);
  /** 일괄수정 창 닫기 — 그 창이 지금 열린 워크플로를 바꿔 저장했으면 새로고침을 묻는다 (openWorkflow) */
  const closeBulk = () => {
    setBulkOpen(false);
    checkOpenWorkflowFreshness(store).catch((e) => showAlert(String((e as Error)?.message || e)));
  };

  const HeaderActionsStart = () => {
    const { t } = useTranslation();
    return (
      <div className="flex items-center gap-4">
        <button
          className="text-sm px-3 py-1.5 text-gray-700 hover:bg-gray-100 rounded-md border border-gray-300 flex items-center gap-1 whitespace-nowrap shrink-0"
          onClick={() => setDataDefModalOpen(true)}
          data-point="header:dataDef"
          title={t('layout:system.dataDefTitle')}
        >
          <span>🗂</span> {t('layout:system.dataDef')}
        </button>
        <button
          className="text-sm px-3 py-1.5 text-gray-700 hover:bg-gray-100 rounded-md border border-gray-300 flex items-center gap-1 whitespace-nowrap shrink-0"
          onClick={() => setVarsOpen(true)}
          data-point="header:vars"
          title={t('layout:system.varsTitle')}
        >
          <span>𝑥</span> {t('layout:system.vars')}
        </button>
        <button
          className="text-sm px-3 py-1.5 text-gray-700 hover:bg-gray-100 rounded-md border border-gray-300 flex items-center gap-1 whitespace-nowrap shrink-0"
          onClick={() => setConnsOpen(true)}
          data-point="header:conns"
          title={t('layout:system.connsTitle')}
        >
          <span>🗄</span> {t('layout:system.conns')}
        </button>
        <button
          className="text-sm px-3 py-1.5 text-gray-700 hover:bg-gray-100 rounded-md border border-gray-300 flex items-center gap-1 whitespace-nowrap shrink-0"
          onClick={() => setConnLimitsOpen(true)}
          data-point="header:connLimits"
          title={t('layout:system.connLimitsTitle')}
        >
          <span>🔌</span> {t('layout:system.connLimits')}
        </button>
        <button
          className="text-sm px-3 py-1.5 text-gray-700 hover:bg-gray-100 rounded-md border border-gray-300 flex items-center gap-1 whitespace-nowrap shrink-0"
          onClick={() => setTemplatesOpen(true)}
          data-point="header:templates"
          title={t('layout:system.templatesTitle')}
        >
          <span>🧩</span> {t('layout:system.templates')}
        </button>
        <button
          className="text-sm px-3 py-1.5 text-gray-700 hover:bg-gray-100 rounded-md border border-gray-300 flex items-center gap-1 whitespace-nowrap shrink-0"
          onClick={() => setBulkOpen(true)}
          data-point="header:bulk"
          title={t('layout:system.bulkTitle')}
        >
          <span>🔎</span> {t('layout:system.bulk')}
        </button>
        <button
          className="text-sm px-3 py-1.5 text-gray-700 hover:bg-gray-100 rounded-md border border-gray-300 flex items-center gap-1 whitespace-nowrap shrink-0"
          onClick={() => setBulkNewOpen(true)}
          data-point="header:bulkNew"
          title={t('layout:system.bulkNewTitle')}
        >
          <span>📑</span> {t('layout:system.bulkNew')}
        </button>
        <button
          className="text-sm px-3 py-1.5 text-gray-700 hover:bg-gray-100 rounded-md border border-gray-300 flex items-center gap-1 whitespace-nowrap shrink-0"
          onClick={() => setExplorerOpen(true)}
          data-point="header:explorer"
          title={t('layout:system.explorerTitle')}
        >
          <span>📁</span> {t('layout:system.explorer')}
        </button>
      </div>
    );
  };

  const AiButton = () => {
    const { t } = useTranslation();
    const { open } = useAiDrawer();
    return (
      <button
        className={`text-sm px-3 py-1.5 rounded-md border flex items-center gap-1 ${open
          ? 'border-blue-500 bg-blue-50 text-blue-700'
          : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
        onClick={() => setAiDrawer({ open: !open })}
        data-point="header:ai"
        title={open ? t('layout:ai.close') : t('layout:ai.openTitle')}
        aria-pressed={open}
      >
        <span>✨</span> {t('layout:ai.title')}
      </button>
    );
  };

  const { toolbox, canvas, property } = children;
  const dock = useDockPrefs();

  return (
    // h-full: 상위(App 의 탭 바 아래 영역)가 높이를 정한다 — h-screen 을 쓰면 탭 바 높이만큼 화면 밖으로 넘친다.
    // 본문은 flex-1 + min-h-0 으로 남은 높이를 정확히 차지하고, 각 컬럼은 그 안에서 스스로 스크롤한다.
    <div className="h-full flex flex-col bg-gray-50 overflow-hidden">
      {/* 시스템 전체에 대한 버튼(데이터 정의·전역변수·연결…) */}
      <Header
        className="z-10 shrink-0"
        // 프로젝트 메뉴는 HeaderActionsStart 밖에 둔다 — 그 안은 렌더마다 새 컴포넌트라, 펼침·진행 중인 불러오기 상태가 사라진다
        headerStart={<div className="flex items-center"><ProjectMenu /><HeaderActionsStart /></div>}
        // AI 에이전트 — 줄 맨 오른쪽. 오른쪽에서 펼쳐지는 창을 연다/닫는다. 쓸 수 없으면(끔·내부망) 버튼도 없다
        headerEnd={assistantOn ? <AiButton /> : undefined}
      />
      <div className="flex-1 min-h-0 flex overflow-hidden">
        {/* 탐색기·툴박스는 각각 접을 수 있다 — 접으면 얇은 막대만 남는다(CollapsibleSide) */}
        <CollapsibleSide id="explorer" label={t('layout:explorer.heading')}><WorkflowExplorer /></CollapsibleSide>
        {/* 워크플로 영역(툴박스·캔버스·속성창) — 지금 연 워크플로에 대한 버튼(되돌리기~실행)은 캔버스 오른쪽 위 도구 모음에 있다 (canvas/WorkflowActions) */}
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex-1 min-h-0 flex overflow-hidden">
            <CollapsibleSide id="toolbox" label={t('common:ui.toolbox')}>{toolbox}</CollapsibleSide>
            {/* 노드 속성은 캔버스 오른쪽 또는 아래 — 보는 사람이 고른다(propertyDock) */}
            <div className="flex-1 min-w-0 flex flex-col min-h-0">
              <main className="flex-1 min-h-0 min-w-0 flex">
                {canvas}
              </main>
              {dock.position === 'bottom' && <PropertyDock position="bottom">{property}</PropertyDock>}
            </div>
            {dock.position === 'right' && <PropertyDock position="right">{property}</PropertyDock>}
          </div>
        </div>
        {/* AI 에이전트 창 — 워크플로 영역을 옆으로 밀며 펼쳐진다(노드 속성을 가리지 않는다) */}
        {assistantOn && <AiDrawer />}
      </div>
      <RunStatusSync />
      {dataDefModalOpen && <DataDefinitionModal onClose={() => setDataDefModalOpen(false)} />}
      <DataNodeViewHost />
      <WorkflowPreviewHost />
      {varsOpen && <GlobalVariablesDialog onClose={() => setVarsOpen(false)} />}
      {connsOpen && <ConnectionsDialog onClose={() => setConnsOpen(false)} />}
      {connLimitsOpen && <ConnectionLimitsDialog onClose={() => setConnLimitsOpen(false)} />}
      {templatesOpen && <TemplateManagerDialog onClose={() => setTemplatesOpen(false)} />}
      {/* 속성 일괄수정 — 화면이 넓어야 해서 큰 창으로 띄운다 */}
      {bulkOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-40" onMouseDown={closeBulk}>
          <div className="bg-white rounded-lg shadow-2xl w-[1280px] max-w-[96vw] h-[88vh] flex flex-col overflow-hidden" onMouseDown={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 shrink-0">
              <span className="text-sm font-semibold text-gray-800">{t('layout:bulk.title')}</span>
              <button className="px-2 py-1 text-xs rounded hover:bg-gray-100 text-gray-600" onClick={closeBulk} title={t('layout:bulk.close')}>✕</button>
            </div>
            <div className="flex-1 min-h-0 overflow-auto">
              <BulkEditScreen />
            </div>
          </div>
        </div>
      )}
      {/* 워크플로 일괄 등록 — 표가 넓어 큰 창으로 */}
      {bulkNewOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-40" onMouseDown={() => setBulkNewOpen(false)}>
          <div className="bg-white rounded-lg shadow-2xl w-[1280px] max-w-[96vw] h-[88vh] flex flex-col overflow-hidden" onMouseDown={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 shrink-0">
              <span className="text-sm font-semibold text-gray-800">{t('layout:system.bulkNew')}</span>
              <button className="px-2 py-1 text-xs rounded hover:bg-gray-100 text-gray-600" onClick={() => setBulkNewOpen(false)} title={t('layout:bulk.close')}>✕</button>
            </div>
            <div className="flex-1 min-h-0">
              <BulkCreateScreen />
            </div>
          </div>
        </div>
      )}
      {explorerOpen && <ServerExplorerDialog onClose={() => setExplorerOpen(false)} />}
    </div>
  );
};

export default ModernLayout; 